import filter from "lodash/filter";
import { useKPIsReviewSidepanelActions } from "PFApp/components/kpis_review_sidepanel/use_kpis_review_sidepanel_actions";
import { Alert } from "PFComponents/alert";
import { useKPIResults } from "PFCore/hooks/queries/kpi";
import { KPIResult, KPIStatus } from "PFCore/types/kpi";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useCurrentActivity } from "../../hooks/use_current_activity";
import css from "../activity_page.module.scss";

type ActivityKPIAlertProps = {
  engagementId: number;
};

type KPIAlertData = {
  kind: "error" | "warning";
  title: string;
};

export const ActivityKPIAlert = ({ engagementId }: ActivityKPIAlertProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.kpi" });
  const activity = useCurrentActivity();
  const { openKPIsReviewSidepanel } = useKPIsReviewSidepanelActions();

  const { data: kpiResults } = useKPIResults(
    { engagementId },
    {
      enabled: !!engagementId
    }
  );

  const handleReviewClick = () => {
    openKPIsReviewSidepanel({
      engagementId: activity.id,
      engagementName: activity.name,
      KPIs: kpiResults || []
    });
  };

  const alertData: KPIAlertData | null = useMemo(() => {
    if (!kpiResults?.length) {
      return null;
    }

    const approvalRequired = filter(
      kpiResults,
      ({ status }: KPIResult) => status === KPIStatus.ApprovalRequired
    );
    if (approvalRequired.length > 0) {
      return {
        kind: "error",
        title: t("alertApprovalRequired", { count: approvalRequired.length })
      };
    }

    const approvalRejected = filter(
      kpiResults,
      ({ status }: KPIResult) => status === KPIStatus.ApprovalRejected
    );
    if (approvalRejected.length > 0) {
      return {
        kind: "error",
        title: t("alertApprovalRejected", { count: approvalRejected.length })
      };
    }

    const approvalRequested = filter(
      kpiResults,
      ({ status }: KPIResult) => status === KPIStatus.ApprovalRequested
    );
    if (approvalRequested.length > 0) {
      return {
        kind: "warning",
        title: t("alertApprovalRequested", { count: approvalRequested.length })
      };
    }

    return null;
  }, [kpiResults, t]);

  if (!alertData) {
    return null;
  }

  return (
    <Alert
      className={css.kpiExceptionAlert}
      kind={alertData.kind}
      iconCentered
      title={alertData.title}
      action={{
        text: t("review"),
        onClick: handleReviewClick
      }}
    />
  );
};
